/**
 * useSchemaMetadata - Hook para buscar metadata de schema do banco
 *
 * Implementa Schema-driven UI: o frontend reflete automaticamente
 * as constraints NOT NULL do banco de dados.
 *
 * @example
 * const { getRequiredFields, isFieldRequired, loading } = useSchemaMetadata();
 *
 * // Verificar se um campo específico é required
 * const isNameRequired = isFieldRequired('pessoa', 'nome'); // true se NOT NULL
 *
 * // Obter todos os campos required de uma tabela
 * const requiredFields = getRequiredFields('pessoa'); // ['nome', 'tenant_id', ...]
 *
 * // Enriquecer config de fields automaticamente
 * const enrichedFields = enrichFieldsWithRequired('pessoa', fields);
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

type FieldMeta = { required: boolean; nullable: boolean; hasDefault: boolean };
type TableSchema = Record<string, FieldMeta>;
type SchemaCache = Record<string, TableSchema>;

// Cache global para evitar múltiplas requisições
let schemaCache: SchemaCache | null = null;
let schemaCachePromise: Promise<SchemaCache> | null = null;

/**
 * Busca metadata de schema do banco de dados
 * Campos com is_nullable = 'NO' são considerados required
 */
async function fetchSchemaMetadata() {
  // Se já tem cache, retorna
  if (schemaCache) {
    return schemaCache;
  }

  // Se já tem uma requisição em andamento, aguarda
  if (schemaCachePromise) {
    return schemaCachePromise;
  }

  // Schema fetch iniciado

  // Faz a requisição
  schemaCachePromise = (async () => {
    try {
      // IMPORTANTE: Supabase/PostgREST tem limite de 1000 linhas por requisição
      // A RPC retorna ~2800+ linhas, então fazemos múltiplas chamadas
      let allData: any[] = [];
      let offset = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await (supabase as any)
          .rpc('get_schema_required_fields')
          .range(offset, offset + pageSize - 1);

        if (error) {
          console.error('[useSchemaMetadata] RPC erro:', error);
          return await fetchSchemaMetadataFallback();
        }

        if (data && data.length > 0) {
          allData = [...allData, ...data];
          offset += pageSize;
          hasMore = data.length === pageSize; // Se retornou menos que pageSize, acabou
        } else {
          hasMore = false;
        }
      }

      schemaCache = processSchemaData(allData);
      cacheVersion = CACHE_VERSION;

      return schemaCache;
    } catch (err) {
      console.error('[useSchemaMetadata] Erro ao buscar schema:', err);
      return {};
    } finally {
      schemaCachePromise = null;
    }
  })();

  return schemaCachePromise;
}

/**
 * Fallback: busca via query direta (se RPC não existir)
 */
async function fetchSchemaMetadataFallback(): Promise<SchemaCache> {
  try {
    // Tenta buscar de uma edge function ou endpoint alternativo
    const response = await fetch('/api/schema-metadata');
    if (response.ok) {
      const data = await response.json();
      return processSchemaData(data);
    }
  } catch (err) {
    // Silently fail - usará os defaults do frontend
  }
  return {};
}

/**
 * Processa os dados do schema em um formato otimizado para lookup
 */
function processSchemaData(data: any): SchemaCache {
  if (!data || !Array.isArray(data)) {
    return {};
  }

  const schema: SchemaCache = {};

  // Campos de auditoria que nunca devem ser required no formulário
  const auditFields = [
    'id', 'created_at', 'updated_at', 'deleted_at',
    'created_by', 'updated_by', 'deleted_by',
    'tenant_id', 'filial_id'
  ];

  data.forEach((row: any) => {
    const { table_name, column_name, is_nullable, column_default } = row;

    // Ignora campos de auditoria
    if (auditFields.includes(column_name)) return;

    // Inicializa tabela se não existir
    if (!schema[table_name]) {
      schema[table_name] = {};
    }

    // Campo é required se:
    // 1. is_nullable = 'NO' (NOT NULL no banco)
    // 2. NÃO tem default value (se tem default, não precisa preencher)
    const isRequired = is_nullable === 'NO' && !column_default;

    schema[table_name][column_name] = {
      required: isRequired,
      nullable: is_nullable === 'YES',
      hasDefault: !!column_default
    };
  });

  return schema;
}

// Singleton para garantir que a busca só aconteça uma vez
let fetchStarted = false;
let subscribers = new Set<(cache: SchemaCache | null) => void>();

// Versão do cache - incrementar quando mudar a lógica de processamento
const CACHE_VERSION = 3;
let cacheVersion: number | null = null;

// Verificar se o cache precisa ser invalidado por mudança de versão
function checkCacheVersion() {
  if (schemaCache && cacheVersion !== CACHE_VERSION) {
    // Cache antigo detectado, limpando...
    schemaCache = null;
    schemaCachePromise = null;
    fetchStarted = false;
    cacheVersion = CACHE_VERSION;
    return true;
  }
  return false;
}

// Expor funções para debug no console do browser
if (typeof window !== 'undefined') {
  (window as any).clearSchemaCache = () => {
    schemaCache = null;
    schemaCachePromise = null;
    fetchStarted = false;
    cacheVersion = null;
    subscribers.clear();
  };

  // Expor cache para inspeção
  (window as any).getSchemaCache = () => {
    return schemaCache;
  };
}

/**
 * Notifica todos os subscribers quando o schema é atualizado
 */
function notifySubscribers() {
  subscribers.forEach(callback => callback(schemaCache));
}

/**
 * Inicia o fetch se ainda não iniciou
 */
function startFetchIfNeeded() {
  if (schemaCache || fetchStarted) {
    return;
  }

  fetchStarted = true;

  fetchSchemaMetadata().then(() => {
    notifySubscribers();
  }).catch(err => {
    console.error('[useSchemaMetadata] Erro no fetch:', err);
  });
}

/**
 * Subscribe para atualizações do schema
 */
function subscribeToSchema(callback: (cache: SchemaCache | null) => void) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Hook principal
 */
export function useSchemaMetadata() {
  // Força re-render quando schema muda
  const [, forceUpdate] = useState(0);

  // Verificar se o cache precisa ser invalidado por mudança de versão
  checkCacheVersion();

  // Sempre inicia o fetch se necessário (mesmo antes do useEffect)
  if (!schemaCache && !fetchStarted) {
    startFetchIfNeeded();
  }

  useEffect(() => {
    // Subscribe para receber atualizações
    const unsubscribe = subscribeToSchema(() => {
      forceUpdate(n => n + 1);
    });

    // Se o cache foi carregado enquanto o componente estava montando, força update
    if (schemaCache && Object.keys(schemaCache).length > 0) {
      forceUpdate(n => n + 1);
    }

    return () => { unsubscribe(); };
  }, []);

  // Usa schemaCache diretamente (global) - o forceUpdate garante re-render quando muda
  const schema = schemaCache || {};
  const loading = !schemaCache && fetchStarted;
  const error = null; // Erros são logados no console

  /**
   * Verifica se um campo específico é required
   * Lê diretamente do schemaCache para garantir dados atualizados
   */
  const isFieldRequired = useCallback((tableName: string, fieldName: string) => {
    return schemaCache?.[tableName]?.[fieldName]?.required ?? false;
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Retorna lista de campos required de uma tabela
   */
  const getRequiredFields = useCallback((tableName: string): string[] => {
    const tableSchema = schemaCache?.[tableName];
    if (!tableSchema) return [];

    return Object.entries(tableSchema)
      .filter(([_, meta]) => (meta as FieldMeta).required)
      .map(([fieldName]) => fieldName);
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Enriquece array de fields com informação de required do banco
   * Preserva required: true explícito no código (override manual)
   */
  const enrichFieldsWithRequired = useCallback((tableName: string, fields: any[]) => {
    if (!fields || !Array.isArray(fields)) return fields;

    return fields.map((field: any) => {
      const fieldName = field.field || field.name;
      const schemaRequired = schemaCache?.[tableName]?.[fieldName]?.required ?? false;

      // Se já tem required: true no código, mantém
      // Se o schema diz que é required, adiciona
      return {
        ...field,
        required: field.required === true || schemaRequired
      };
    });
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    schema,
    loading,
    error,
    isFieldRequired,
    getRequiredFields,
    enrichFieldsWithRequired
  };
}

/**
 * Função utilitária para uso fora de componentes React
 * Útil para validações síncronas quando o schema já foi carregado
 */
export function getSchemaCache() {
  return schemaCache;
}

/**
 * Força recarregamento do cache (útil após migrations)
 */
export function invalidateSchemaCache() {
  schemaCache = null;
  schemaCachePromise = null;
  fetchStarted = false;
  subscribers.clear();
}

export default useSchemaMetadata;
